import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, gql } from "@apollo/client";
import { useLang } from "../../context/lang-context";
import { dineInApi, TableInfo, AddProductItem } from "../../services/dineInApi";
import "./DineInOrderPage.css";

const BASE_URL = "https://fooapp.mx";
const TOKEN_STORAGE_KEY = "dine_table_token";

/* ========== GraphQL ========== */

const GET_RESTAURANT = gql`
  query GetRestaurant($id: Int!) {
    getRestaurant(id: $id) {
      id
      company_name
      logo
      cover_img
      products {
        id
        title
        description
        price
        has_size
        snoozed
        status_web
        images {
          id
          url
          type
        }
        category {
          id
          name
          name_en
        }
        sizes {
          id
          name
          name_en
          price
        }
        ingredients {
          id
          name
          name_en
          color
          icon
        }
        options {
          id
          id_ingredient
          price_mod
          ingredient {
            id
            name
            name_en
          }
        }
      }
    }
  }
`;

/* ========== Interfaces ========== */

interface ProductImage {
  id: string;
  url: string;
  type: string;
}

interface Category {
  id: string;
  name: string;
  name_en: string;
}

interface Size {
  id: string;
  name: string;
  name_en: string;
  price: number;
}

interface Ingredient {
  id: string;
  name: string;
  name_en: string;
  color: string | null;
  icon: string | null;
}

interface OptionIngredient {
  id: string;
  name: string;
  name_en: string;
}

interface Option {
  id: string;
  id_ingredient: string;
  price_mod: number;
  ingredient: OptionIngredient;
}

interface Product {
  id: string;
  title: string;
  description: string;
  price: number;
  has_size: boolean;
  snoozed: boolean;
  status_web: boolean;
  images: ProductImage[];
  category: Category | null;
  sizes: Size[];
  ingredients: Ingredient[];
  options: Option[];
}

interface AddedItem {
  id: string;
  title: string;
  price: number;
}

/* ========== Helpers ========== */

function resolveImageUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${BASE_URL}${path}`;
}

function formatPrice(price: number | string | null | undefined): string {
  const num = Number(price) || 0;
  return `$${num.toFixed(2)}`;
}

function getGuestNameKey(token: string): string {
  return `dine_guest_name_${token}`;
}

/* ========== Main Component ========== */

const DineInOrderPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { lang, setLang, t } = useLang();

  // Token: captured from URL once, then stripped; persisted in localStorage.
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");
    if (tokenFromUrl) {
      localStorage.setItem(TOKEN_STORAGE_KEY, tokenFromUrl);
      setToken(tokenFromUrl);
      setSearchParams({}, { replace: true });
    } else {
      setToken(localStorage.getItem(TOKEN_STORAGE_KEY));
    }
    // Only run once on mount: the URL token is a one-time capture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Table / session state
  const [tableInfo, setTableInfo] = useState<TableInfo | null>(null);
  const [tableLoading, setTableLoading] = useState(true);
  const [tableError, setTableError] = useState<string | null>(null);

  // Guest state
  const [guestName, setGuestName] = useState<string>("");
  const [nameInput, setNameInput] = useState("");

  // Added-items state (this device's session only)
  const [addedItems, setAddedItems] = useState<AddedItem[]>([]);
  const [addedExpanded, setAddedExpanded] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState<string | null>(null);

  // Product modal state
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedSizeId, setSelectedSizeId] = useState<string | null>(null);
  const [deselectedIngredients, setDeselectedIngredients] = useState<Set<string>>(new Set());
  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(new Set());
  const [productComments, setProductComments] = useState("");
  const [addingProduct, setAddingProduct] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Fetch table info once we know the token
  useEffect(() => {
    if (token === null) return;
    if (!token) {
      setTableLoading(false);
      return;
    }
    setTableLoading(true);
    dineInApi
      .getTableInfo(token)
      .then((data) => {
        setTableInfo(data);
        const savedName = localStorage.getItem(getGuestNameKey(token));
        if (savedName) setGuestName(savedName);
      })
      .catch((err) => {
        setTableError(err.message || t("Mesa no encontrada", "Table not found"));
      })
      .finally(() => setTableLoading(false));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch restaurant menu
  const { data: restaurantData, loading: restaurantLoading } = useQuery(GET_RESTAURANT, {
    variables: { id: tableInfo?.restaurant_id },
    skip: !tableInfo?.restaurant_id,
  });

  const restaurant = restaurantData?.getRestaurant;

  // Group products by category
  const groupedProducts = useMemo(() => {
    if (!restaurant?.products) return {};
    const groups: Record<string, Product[]> = {};
    restaurant.products
      .filter((p: Product) => p.status_web && !p.snoozed)
      .forEach((product: Product) => {
        const catName = product.category
          ? lang === "es"
            ? product.category.name
            : product.category.name_en || product.category.name
          : lang === "es"
          ? "Otros"
          : "Other";
        if (!groups[catName]) groups[catName] = [];
        groups[catName].push(product);
      });
    return groups;
  }, [restaurant, lang]);

  // Compute modal product price
  const computedPrice = useMemo(() => {
    if (!selectedProduct) return 0;
    let price = selectedProduct.price;

    if (selectedSizeId) {
      const size = selectedProduct.sizes.find((s) => s.id === selectedSizeId);
      if (size) price = size.price;
    } else if (selectedProduct.has_size && selectedProduct.sizes.length > 0) {
      price = selectedProduct.sizes[0].price;
    }

    selectedOptions.forEach((optId) => {
      const opt = selectedProduct.options.find((o) => o.id === optId);
      if (opt) price += opt.price_mod;
    });

    return price;
  }, [selectedProduct, selectedSizeId, selectedOptions]);

  /* ========== Handlers ========== */

  const handleSaveName = () => {
    if (!token || !nameInput.trim()) return;
    const trimmed = nameInput.trim().slice(0, 20);
    localStorage.setItem(getGuestNameKey(token), trimmed);
    setGuestName(trimmed);
  };

  const openProductModal = (product: Product) => {
    setSelectedProduct(product);
    if (product.has_size && product.sizes.length > 0) {
      setSelectedSizeId(product.sizes[0].id);
    } else {
      setSelectedSizeId(null);
    }
    setDeselectedIngredients(new Set());
    setSelectedOptions(new Set());
    setProductComments("");
    setAddError(null);
  };

  const closeProductModal = () => {
    setSelectedProduct(null);
  };

  const toggleIngredient = (id: string) => {
    setDeselectedIngredients((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleOption = (id: string) => {
    setSelectedOptions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAddProduct = useCallback(async () => {
    if (!token || !guestName || !selectedProduct) return;
    setAddingProduct(true);
    setAddError(null);
    try {
      const activeIngredients = selectedProduct.ingredients
        .filter((ing) => !deselectedIngredients.has(ing.id))
        .map((ing) => (lang === "es" ? ing.name : ing.name_en || ing.name));
      const activeOptions = selectedProduct.options
        .filter((opt) => selectedOptions.has(opt.id))
        .map((opt) =>
          lang === "es" ? opt.ingredient.name : opt.ingredient.name_en || opt.ingredient.name
        );
      const ingredientsTxt = [...activeIngredients, ...activeOptions].join(", ");

      const product: AddProductItem = {
        id: parseInt(selectedProduct.id),
        price: computedPrice,
        size: selectedSizeId ? parseInt(selectedSizeId) : null,
        ingredients: Array.from(deselectedIngredients).map((id) => parseInt(id)),
        ingredients_opt: Array.from(selectedOptions).map((id) => parseInt(id)),
        ingredients_txt: ingredientsTxt,
        comments: productComments || undefined,
      };

      await dineInApi.addProducts(token, {
        guest_name: guestName,
        products: [product],
      });

      setAddedItems((prev) => [
        ...prev,
        { id: `${Date.now()}`, title: selectedProduct.title, price: computedPrice },
      ]);
      setConfirmationMessage(
        t("Agregado a tu mesa", "Added to your table")
      );
      setTimeout(() => setConfirmationMessage(null), 2500);
      closeProductModal();
    } catch (err: any) {
      setAddError(err.message || t("Error al agregar producto", "Error adding product"));
    } finally {
      setAddingProduct(false);
    }
  }, [token, guestName, selectedProduct, deselectedIngredients, selectedOptions, selectedSizeId, productComments, computedPrice, lang, t]);

  /* ========== Renders ========== */

  if (tableLoading || token === null) {
    return (
      <div className="di-loading">
        <div className="di-spinner" />
      </div>
    );
  }

  if (!token || tableError || !tableInfo) {
    return (
      <div className="di-error">
        <img src="/img/logo_svg.svg" alt="Foo" className="di-error-logo" />
        <h2>{t("Mesa no encontrada", "Table not found")}</h2>
        <p>
          {tableError ||
            t(
              "Este codigo QR no es valido. Por favor pide ayuda al personal del restaurante.",
              "This QR code is not valid. Please ask the restaurant staff for help."
            )}
        </p>
      </div>
    );
  }

  const logoUrl = resolveImageUrl(tableInfo.restaurant_logo);
  const addedTotal = addedItems.reduce((sum, i) => sum + i.price, 0);

  // Name entry screen
  if (!guestName) {
    return (
      <div className="di-join">
        <div className="di-join-card">
          {logoUrl && (
            <img src={logoUrl} alt={tableInfo.restaurant_name} className="di-join-logo" />
          )}
          <h1 className="di-join-restaurant">{tableInfo.restaurant_name}</h1>
          {tableInfo.table_name && (
            <p className="di-join-table">
              {t("Mesa", "Table")} {tableInfo.table_name}
            </p>
          )}
          <p className="di-join-invite">
            {t(
              "Escribe tu nombre para ordenar desde tu celular",
              "Enter your name to order from your phone"
            )}
          </p>

          <div className="di-join-form">
            <input
              type="text"
              className="di-join-input"
              placeholder={t("Tu nombre", "Your name")}
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value.slice(0, 20))}
              maxLength={20}
              onKeyDown={(e) => e.key === "Enter" && handleSaveName()}
            />
            <button
              className="di-join-btn"
              onClick={handleSaveName}
              disabled={!nameInput.trim()}
            >
              {t("Continuar", "Continue")}
            </button>
          </div>
        </div>

        <div className="di-join-footer">
          <img src="/img/logo_svg.svg" alt="Foo" className="di-join-footer-logo" />
          <span>
            {t("Impulsado por", "Powered by")} <strong>Foo</strong>
          </span>
        </div>
      </div>
    );
  }

  // Menu view
  const coverUrl = resolveImageUrl(restaurant?.cover_img);
  const restLogoUrl = resolveImageUrl(restaurant?.logo);

  return (
    <div className="di-page">
      <nav className="di-nav">
        <div className="container d-flex align-items-center justify-content-between">
          <img src="/img/logo_svg.svg" alt="Foo" className="di-nav-logo" />
          <div className="di-nav-actions">
            <span className="di-guest-badge">
              <i className="fas fa-user"></i> {guestName}
            </span>
            <button
              className="btn btn-outline-dark btn-sm di-lang-btn"
              onClick={() => setLang(lang === "es" ? "en" : "es")}
            >
              {lang === "es" ? "EN" : "ES"}
            </button>
          </div>
        </div>
      </nav>

      {confirmationMessage && (
        <div className="di-toast">{confirmationMessage}</div>
      )}

      {restaurantLoading ? (
        <div className="di-restaurant-loading">
          <div className="di-spinner" />
        </div>
      ) : (
        <>
          <header
            className="di-cover"
            style={coverUrl ? { backgroundImage: `url(${coverUrl})` } : {}}
          >
            <div className="di-cover-overlay" />
            <div className="container di-cover-content">
              {restLogoUrl && (
                <img src={restLogoUrl} alt={tableInfo.restaurant_name} className="di-cover-logo" />
              )}
              <div>
                <h1 className="di-cover-name">{tableInfo.restaurant_name}</h1>
                {tableInfo.table_name && (
                  <p className="di-cover-table">
                    {t("Mesa", "Table")} {tableInfo.table_name}
                  </p>
                )}
              </div>
            </div>
          </header>

          <div className="container di-menu">
            <h2 className="di-section-title">{t("Menu", "Menu")}</h2>

            {Object.keys(groupedProducts).length === 0 && (
              <p className="di-empty">
                {t("No hay productos disponibles.", "No products available.")}
              </p>
            )}

            {Object.entries(groupedProducts).map(([category, products]) => (
              <div key={category} className="di-category">
                <h3 className="di-category-title">{category}</h3>
                <div className="di-products">
                  {products.map((product: Product) => {
                    const image =
                      product.images?.find((img) => img.type === "cover") || product.images?.[0];
                    const imageUrl = resolveImageUrl(image?.url);

                    const priceLabel =
                      product.has_size && product.sizes.length > 0
                        ? `${t("Desde", "From")} ${formatPrice(
                            Math.min(...product.sizes.map((s) => s.price))
                          )}`
                        : formatPrice(product.price);

                    return (
                      <div
                        key={product.id}
                        className="di-product"
                        onClick={() => openProductModal(product)}
                      >
                        <div className="di-product-info">
                          <h4 className="di-product-title">{product.title}</h4>
                          {product.description && (
                            <p className="di-product-desc">{product.description}</p>
                          )}
                          <span className="di-product-price">{priceLabel}</span>
                        </div>
                        <div className="di-product-right">
                          {imageUrl && (
                            <img src={imageUrl} alt={product.title} className="di-product-img" />
                          )}
                          <button
                            className="di-add-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              openProductModal(product);
                            }}
                            aria-label={t("Agregar", "Add")}
                          >
                            <i className="fas fa-plus"></i>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {addedItems.length > 0 && <div className="di-bottom-spacer" />}
          </div>
        </>
      )}

      {/* Mini "added items" tracker (this device only) */}
      {addedItems.length > 0 && (
        <div className={`di-mini-cart ${addedExpanded ? "di-mini-cart-expanded" : ""}`}>
          {addedExpanded && (
            <div className="di-mini-cart-list">
              <div className="di-mini-cart-header">
                <h4>{t("Tus productos agregados", "Your added items")}</h4>
                <button className="di-mini-cart-close" onClick={() => setAddedExpanded(false)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              {addedItems.map((item) => (
                <div key={item.id} className="di-cart-item">
                  <span className="di-cart-item-name">{item.title}</span>
                  <span className="di-cart-item-price">{formatPrice(item.price)}</span>
                </div>
              ))}
              <div className="di-cart-total-row">
                <span>{t("Total agregado", "Total added")}</span>
                <span className="di-cart-total-price">{formatPrice(addedTotal)}</span>
              </div>
              <p className="di-cart-note">
                {t(
                  "El personal del restaurante vera tu pedido y lo agregara a tu cuenta.",
                  "Restaurant staff will see your order and add it to your bill."
                )}
              </p>
            </div>
          )}

          <div className="di-mini-cart-bar" onClick={() => setAddedExpanded(!addedExpanded)}>
            <div className="di-mini-cart-bar-left">
              <span className="di-cart-count">{addedItems.length}</span>
              <span className="di-cart-label">
                {addedItems.length === 1
                  ? t("producto agregado", "item added")
                  : t("productos agregados", "items added")}
              </span>
            </div>
            <span className="di-cart-bar-total">{formatPrice(addedTotal)}</span>
            <i className={`fas fa-chevron-${addedExpanded ? "down" : "up"}`}></i>
          </div>
        </div>
      )}

      {/* Product Customization Modal */}
      {selectedProduct && (
        <div className="di-modal-overlay" onClick={closeProductModal}>
          <div className="di-modal" onClick={(e) => e.stopPropagation()}>
            <button className="di-modal-close" onClick={closeProductModal}>
              <i className="fas fa-times"></i>
            </button>

            <div className="di-modal-body">
              {(() => {
                const img =
                  selectedProduct.images?.find((i) => i.type === "cover") ||
                  selectedProduct.images?.[0];
                const url = resolveImageUrl(img?.url);
                return url ? (
                  <img src={url} alt={selectedProduct.title} className="di-modal-img" />
                ) : null;
              })()}

              <div className="di-modal-content">
                <h3 className="di-modal-title">{selectedProduct.title}</h3>
                {selectedProduct.description && (
                  <p className="di-modal-desc">{selectedProduct.description}</p>
                )}
                <p className="di-modal-price">{formatPrice(computedPrice)}</p>

                {selectedProduct.has_size && selectedProduct.sizes.length > 0 && (
                  <div className="di-modal-section">
                    <h4 className="di-modal-section-title">{t("Tamano", "Size")}</h4>
                    <div className="di-sizes">
                      {selectedProduct.sizes.map((size) => (
                        <label key={size.id} className="di-size-option">
                          <input
                            type="radio"
                            name="product-size"
                            checked={selectedSizeId === size.id}
                            onChange={() => setSelectedSizeId(size.id)}
                          />
                          <span className="di-size-label">
                            {lang === "es" ? size.name : size.name_en || size.name} -{" "}
                            {formatPrice(size.price)}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {selectedProduct.ingredients.length > 0 && (
                  <div className="di-modal-section">
                    <h4 className="di-modal-section-title">{t("Ingredientes", "Ingredients")}</h4>
                    <div className="di-ingredients">
                      {selectedProduct.ingredients.map((ing) => (
                        <label key={ing.id} className="di-ingredient-option">
                          <input
                            type="checkbox"
                            checked={!deselectedIngredients.has(ing.id)}
                            onChange={() => toggleIngredient(ing.id)}
                          />
                          <span className="di-ingredient-label">
                            {lang === "es" ? ing.name : ing.name_en || ing.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {selectedProduct.options.length > 0 && (
                  <div className="di-modal-section">
                    <h4 className="di-modal-section-title">{t("Extras", "Extras")}</h4>
                    <div className="di-options">
                      {selectedProduct.options.map((opt) => (
                        <label key={opt.id} className="di-option-item">
                          <input
                            type="checkbox"
                            checked={selectedOptions.has(opt.id)}
                            onChange={() => toggleOption(opt.id)}
                          />
                          <span className="di-option-label">
                            {lang === "es"
                              ? opt.ingredient.name
                              : opt.ingredient.name_en || opt.ingredient.name}
                            {opt.price_mod > 0 && (
                              <span className="di-option-price"> +{formatPrice(opt.price_mod)}</span>
                            )}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="di-modal-section">
                  <h4 className="di-modal-section-title">{t("Comentarios", "Comments")}</h4>
                  <textarea
                    className="di-comments-input"
                    placeholder={t("Instrucciones especiales...", "Special instructions...")}
                    value={productComments}
                    onChange={(e) => setProductComments(e.target.value)}
                    rows={2}
                  />
                </div>

                {addError && <p className="di-modal-error">{addError}</p>}
              </div>
            </div>

            <button className="di-modal-add-btn" onClick={handleAddProduct} disabled={addingProduct}>
              {addingProduct ? (
                <span className="di-btn-spinner" />
              ) : (
                <>
                  {t("Agregar", "Add")} {formatPrice(computedPrice)}
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DineInOrderPage;
